import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  signal,
} from '@angular/core';

export type SnackbarVariant = 'success' | 'error' | 'info';

export interface SnackbarHostState {
  id: number;
  message: string;
  variant: SnackbarVariant;
  closeLabel: string;
}

/**
 * Renders the stack of active toasts in a fixed-position viewport corner.
 * Created once by SnackbarService and re-used across the app lifetime.
 *
 * Encapsulation: None — attached imperatively to document.body, so the
 * default Angular emulated scoping would invalidate every selector.
 */
@Component({
  selector: 'app-snackbar-host',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="app-snackbar-host">
      @for (toast of toasts(); track toast.id) {
        <div
          class="app-snackbar"
          [class.app-snackbar--success]="toast.variant === 'success'"
          [class.app-snackbar--error]="toast.variant === 'error'"
          [class.app-snackbar--info]="toast.variant === 'info'"
          role="status"
          aria-live="polite">
          <span class="app-snackbar__msg">{{ toast.message }}</span>
          <button
            type="button"
            class="app-snackbar__close"
            (click)="dismiss(toast.id)">
            {{ toast.closeLabel }}
          </button>
        </div>
      }
    </div>
  `,
  styleUrls: ['./snackbar-host.component.scss'],
})
export class SnackbarHostComponent {
  protected readonly toasts = signal<SnackbarHostState[]>([]);

  push(entry: SnackbarHostState): void {
    this.toasts.update((list) => [...list, entry]);
  }

  dismiss(id: number): void {
    this.toasts.update((list) => list.filter((t) => t.id !== id));
  }
}
